import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Passwordless and phishing-resistant MFA',
    metaDescription:
      'FIDO2, WebAuthn, hardware keys, and push, applied across web, desktop, VPN, and RDP. Retire passwords from the day-to-day workflow.',
    heroEyebrow: 'Use cases · Passwordless & MFA',
    heroTitle: ['Retire the password.', 'Stop the phishing.'],
    heroBody:
      'FIDO2 and WebAuthn for the workforce, hardware keys for the high-risk roles, and a branded mobile authenticator for everyone else, applied across web, desktop, VPN, and RDP.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'FIDO2 / WebAuthn',
      'MFA on logon, VPN, RDP',
      'Sub-minute enrollment',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Passwordless is a project. Monofor is the platform.',
    pillarsBody:
      'Most passwordless efforts stall on the surfaces that are not the browser. Monofor handles the desktop logon, the VPN, and the privileged terminal, so the password actually retires.',
    pillars: [
      {
        title: 'Passwordless that works in the real world',
        description:
          'FIDO2, WebAuthn, and platform authenticators for the workforce, with smooth fallback paths for the days users lose a phone or forget a key.',
        bullets: [
          'FIDO2 / WebAuthn for web and mobile',
          'Platform authenticators (Windows Hello, Touch ID)',
          'Passwordless macOS sign-in via QR or push',
          'Hardware keys (YubiKey, Titan, Feitian)',
          'Account-recovery paths that do not collapse to "email magic link"',
        ],
      },
      {
        title: 'MFA on every surface, not just the browser',
        description:
          'A passwordless story is incomplete if the Windows logon, VPN, and RDP are still password-only. Monosign covers them all.',
        bullets: [
          'MFA on Windows / macOS / Linux logon',
          'MFA on RDP, SSH, and VPN gateways',
          'MFA on RADIUS-backed infrastructure',
          'Offline OTP for disconnected scenarios',
        ],
      },
      {
        title: 'Branded mobile authenticator',
        description:
          'Push, OTP, and passwordless flows live in a white-label app published under your brand, not in yet another vendor utility.',
        bullets: [
          'Push-with-context approvals',
          'Device-bound keys in Secure Enclave / StrongBox',
          'Biometric unlock',
          'Offline OTP for travelers',
        ],
      },
      {
        title: 'Enrollment in under a minute',
        description:
          'A new user enrolls a factor in less than a minute. Help-desk tickets for "I cannot sign in" drop without a 12-month change program.',
        bullets: [
          'QR-driven self-enrollment',
          'Multi-factor enrollment from a single flow',
          'Step-up enrollment for high-risk roles',
          'Help-desk-assisted recovery flow',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Pick. Enroll. Retire.',
    steps: [
      {
        title: 'Pick your factor strategy',
        description:
          'Decide on platform authenticators, hardware keys, and push as the primary set. TOTP becomes a fallback, not a default.',
      },
      {
        title: 'Enroll the workforce',
        description:
          'Drive QR-based enrollment from the user portal. Risk-tier roles enroll a hardware key in addition.',
      },
      {
        title: 'Retire passwords from the surface',
        description:
          'Cut password use on web, desktop, VPN, and RDP. The password becomes a recovery artifact, not a daily tool.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Multi-Factor Authentication',
        description: 'The MFA capability that powers the passwordless story.',
        href: '/sign/mfa',
      },
      {
        label: 'Monofor Identity',
        description: 'The white-label authenticator app.',
        href: '/sign/authenticator',
      },
      {
        label: 'Zero Trust',
        description: 'Use case: identity-first Zero Trust.',
        href: '/solutions/zero-trust',
      },
    ],
  },
  tr: {
    metaTitle: 'Parolasız ve kimlik avına dirençli MFA',
    metaDescription:
      'FIDO2, WebAuthn, donanım anahtarları ve push; web, masaüstü, VPN ve RDP genelinde uygulanır. Parolaları günlük iş akışından emekli edin.',
    heroEyebrow: 'Kullanım senaryoları · Parolasız ve MFA',
    heroTitle: ['Parolayı emekli edin.', 'Kimlik avını durdurun.'],
    heroBody:
      'Çalışanlar için FIDO2 ve WebAuthn, yüksek riskli roller için donanım anahtarları ve diğer herkes için markanızı taşıyan bir mobil kimlik doğrulayıcı; web, masaüstü, VPN ve RDP genelinde uygulanır.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'FIDO2 / WebAuthn',
      'Oturum açma, VPN ve RDP’de MFA',
      'Bir dakikadan kısa kayıt',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Parolasız geçiş bir projedir. Monofor ise platformu.',
    pillarsBody:
      'Çoğu parolasız girişim, tarayıcı dışındaki yüzeylerde tıkanır. Monofor masaüstü oturum açmayı, VPN’i ve ayrıcalıklı terminali de kapsar; böylece parola gerçekten emekli olur.',
    pillars: [
      {
        title: 'Gerçek dünyada çalışan parolasız deneyim',
        description:
          'Çalışanlar için FIDO2, WebAuthn ve platform kimlik doğrulayıcıları, kullanıcıların telefonunu kaybettiği veya anahtarını unuttuğu günler için sorunsuz yedek yollarla birlikte.',
        bullets: [
          'Web ve mobil için FIDO2 / WebAuthn',
          'Platform kimlik doğrulayıcıları (Windows Hello, Touch ID)',
          'QR veya push ile parolasız macOS oturum açma',
          'Donanım anahtarları (YubiKey, Titan, Feitian)',
          '"Sihirli e-posta bağlantısına" indirgenmeyen hesap kurtarma yolları',
        ],
      },
      {
        title: 'Yalnızca tarayıcıda değil, her yüzeyde MFA',
        description:
          'Windows oturum açma, VPN ve RDP hâlâ yalnızca parolayla korunuyorsa parolasız hikâye eksik demektir. Monosign hepsini kapsar.',
        bullets: [
          'Windows / macOS / Linux oturum açmada MFA',
          'RDP, SSH ve VPN ağ geçitlerinde MFA',
          'RADIUS destekli altyapıda MFA',
          'Bağlantısız senaryolar için çevrimdışı OTP',
        ],
      },
      {
        title: 'Markanızı taşıyan mobil kimlik doğrulayıcı',
        description:
          'Push, OTP ve parolasız akışlar, kendi markanız altında yayımlanan beyaz etiketli bir uygulamada yaşar, bir başka üretici aracında değil.',
        bullets: [
          'Bağlam içeren push onayları',
          'Secure Enclave / StrongBox içinde cihaza bağlı anahtarlar',
          'Biyometrik kilit açma',
          'Seyahat edenler için çevrimdışı OTP',
        ],
      },
      {
        title: 'Bir dakikadan kısa sürede kayıt',
        description:
          'Yeni bir kullanıcı bir faktörü bir dakikadan kısa sürede kaydeder. "Oturum açamıyorum" destek biletleri, 12 aylık bir dönüşüm programı gerekmeden azalır.',
        bullets: [
          'QR ile self servis kayıt',
          'Tek akıştan çok faktörlü kayıt',
          'Yüksek riskli roller için ek adımlı kayıt',
          'Yardım masası destekli kurtarma akışı',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Seçin. Kaydedin. Emekli edin.',
    steps: [
      {
        title: 'Faktör stratejinizi seçin',
        description:
          'Birincil set olarak platform kimlik doğrulayıcılarına, donanım anahtarlarına ve push’a karar verin. TOTP varsayılan değil, yedek olur.',
      },
      {
        title: 'Çalışanları kaydedin',
        description:
          'Kullanıcı portalından QR tabanlı kaydı başlatın. Riskli kademedeki roller ek olarak bir donanım anahtarı kaydeder.',
      },
      {
        title: 'Parolaları yüzeylerden kaldırın',
        description:
          'Web, masaüstü, VPN ve RDP’de parola kullanımını kesin. Parola günlük bir araç değil, kurtarma aracı haline gelir.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Çok Faktörlü Kimlik Doğrulama',
        description: 'Parolasız hikâyeyi çalıştıran MFA yeteneği.',
        href: '/sign/mfa',
      },
      {
        label: 'Monofor Identity',
        description: 'Beyaz etiketli kimlik doğrulayıcı uygulaması.',
        href: '/sign/authenticator',
      },
      {
        label: 'Sıfır Güven',
        description: 'Kullanım senaryosu: kimlik öncelikli Sıfır Güven.',
        href: '/solutions/zero-trust',
      },
    ],
  },
}

type Props = { locale: string }

export default function PasswordlessMfaPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/passwordless-mfa"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
